'use client'

import { BillingDataTable } from '@/components/billing/billing-data-table'
import type { BillingEmployee } from '@/components/billing/billing-table-columns'
import { billingTableColumns } from '@/components/billing/billing-table-columns'
import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { useAuth } from '@/components/providers/auth-provider'
import { Skeleton } from '@/components/ui/skeleton'
import { generateBillingPdf } from '@/lib/pdf_gen/billing-pdf'
import { useTRPC } from '@/trpc/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/employees/billing')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user } = useAuth()
  const api = useTRPC()
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const { data: employees, isPending } = useQuery({
    ...api.employee.getEmployeesForBilling.queryOptions(),
  })

  async function handleExportPdf(employeesToExport: BillingEmployee[]): Promise<void> {
    const businessName = user?.business?.name ?? 'Organization'
    setIsExportingPdf(true)
    const { ResultAsync } = await import('neverthrow')
    const result = await ResultAsync.fromPromise(
      generateBillingPdf(employeesToExport, businessName),
      (error) => error,
    )
    setIsExportingPdf(false)
    result.match(
      () => toast.success('PDF exported successfully'),
      (error) => {
        console.error('PDF export failed:', error)
        toast.error('Failed to export PDF')
      },
    )
  }

  if (isPending)
    return (
      <DashboardShell>
        <DashboardHeader heading="Billing" text="Active employees for billing" />
        <Skeleton className="h-40 w-full" />
      </DashboardShell>
    )

  if (!employees) return <div>No employees found</div>

  return (
    <DashboardShell>
      <DashboardHeader heading="Billing" text="Active employees for billing" />
      <BillingDataTable
        columns={billingTableColumns}
        data={employees}
        onExportPdf={handleExportPdf}
        isExportingPdf={isExportingPdf}
      />
    </DashboardShell>
  )
}
